<template lang="pug">
  div(v-clickoutside="clickOutside").mdrp-root
    div(@click="visible=!visible").mdrp__activator
      slot(name="input")
        v-text-field(v-bind="$attrs" :value="valueString" readonly)
    transition(name="slide-fade" mode="out-in")
      div(:class="pickerStyles()" v-show="visible").mdrp__panel.dropdown-menu
        calendar-ranges(
          v-if="!hidePresets"
          :presets="presets"
          v-model="model"
          @input="apply"
        )
        calendar(
          :day="leftCalendarDay"
          :value="model"
          :hover="hover"
          :show-year-select="showYearSelect"
          :min-year="minYear"
          :max-year="maxYear"
          @input="selectDate"
          @update:hover="hoverDate"
          @clickNextMonth="clickNextMonth"
          @clickPrevMonth="clickPrevMonth"
          @clickYear="d=>selectYear('left', d)"
        ).calendar.left
        calendar(
          :day="rightCalendarDay"
          :value="model"
          :hover="hover"
          :show-year-select="showYearSelect"
          :min-year="minYear"
          :max-year="maxYear"
          @input="selectDate"
          @update:hover="hoverDate"
          @clickNextMonth="clickNextMonth"
          @clickPrevMonth="clickPrevMonth"
          @clickYear="d=>selectYear('right', d)"
        ).calendar.right
</template>

<script>
import datefns from 'date-fns';
import Calendar from './Calendar';
import CalendarRanges from './Ranges';
import clickoutside from './clickoutside';

export default {
  components: { Calendar, CalendarRanges },
  directives: { clickoutside },
  provide () {
    return {
      'picker': this
    };
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        start: Date.now(),
        end: Date.now()
      })
    },
    opens: {
      type: String,
      default: 'left'
    },
    hidePresets: {
      type: Boolean
    },
    // input field
    format: {
      type: String,
      default: 'MMMM DD, YYYY'
    },
    // ranges
    presets: {
      type: Array
    },
    // calendars
    showYearSelect: {
      type: Boolean
    },
    minYear: {
      type: Number
    },
    maxYear: {
      type: Number
    }
  },
  data () {
    return {
      visible: false,
      firstClick: false,
      // calendars
      leftCalendarDay: this.value.start,
      rightCalendarDay: this.value.end,
      // models
      model: {
        start: this.value.start,
        end: this.value.end
      },
      hover: {
        start: this.value.start,
        end: this.value.end
      }
    };
  },
  computed: {
    valueString () {
      const { start, end } = this.value;
      if (datefns.isSameDay(start, end)) return datefns.format(start, this.format);
      return [
        datefns.format(start, this.format),
        datefns.format(end, this.format)
      ].filter(Boolean).join(' - ');
    }
  },
  watch: {
    value (obj) {
      this.model.start = obj.start;
      this.model.end = obj.end;
    },
    model: {
      handler (obj, old) {
        this.hover.start = obj.start;
        this.hover.end = obj.end;
        if (!old || datefns.isSameMonth(obj.start, old.end)) return;
        this.leftCalendarDay = obj.start;
      },
      deep: true
    },
    leftCalendarDay: {
      handler (leftMonth) {
        this.rightCalendarDay = datefns.addMonths(leftMonth, 1).getTime();
      },
      immediate: true
    }
  },
  methods: {
    pickerStyles () {
      return {
        'show-calendar': this.visible,
        'opens-arrow-pos-right': this.opens === 'right',
        'opens-arrow-pos-left': this.opens === 'left',
        'opens-arrow-pos-center': this.opens === 'center'
      };
    },
    selectYear (location, day) {
      this[`${location}CalendarDay`] = day;
    },
    clickNextMonth () {
      this.leftCalendarDay = datefns.addMonths(this.leftCalendarDay, 1).getTime();
    },
    clickPrevMonth () {
      this.leftCalendarDay = datefns.subMonths(this.leftCalendarDay, 1).getTime();
    },
    apply () {
      this.visible = false;
      if (this.firstClick) return (this.firstClick = false);
      this.$emit('input', this.model);
    },
    clickOutside () {
      if (!this.visible) return;
      this.apply();
    },
    hoverDate (hover) {
      if (!this.firstClick) return;
      this.hover = hover;
    },
    selectDate (date) {
      // first click
      if (!this.firstClick) {
        this.firstClick = true;
        this.model.start = date;
        this.model.end = date;
        return;
      }

      // second click
      this.firstClick = false;

      // swap start and end if second click is < the first
      if (datefns.compareAsc(this.model.start, date) > 0) {
        this.model.end = this.model.start;
        this.model.start = date;
      } else {
        this.model.end = date;
      }

      this.apply();
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

/**
 * mixins
 */
 @mixin get-opens-arrow ($val: 7px, $is-before: true) {
  top: -$val;
  $bg: if($is-before, #ccc, #fff);

  border-right: $val solid transparent;
  border-bottom: $val solid $bg;
  border-left: $val solid transparent;
}

%center-block {
  left: 0;
  right: 0;
  width: 0;
  margin-left: auto;
  margin-right: auto;
}

/**
  * 控制箭头在 daterangepicker 中的位置。center的时候，需要 JavaScript 辅助
  * generate style below:
  .opens-arrow-pos-left {
    top: 35px;
    left: 10px;
    right: auto;
  }

  .opens-arrow-pos-left::before {
    left: 9px;
  }
  .opens-arrow-pos-left::after {
    left: 10px;
  }

*/
@mixin control-opens-arrow-pos ($direction: left) {
  $reverse-direction: if($direction == left, right, left);

  &.opens-arrow-pos-#{$direction} {
    top: 40px;
    @if $direction != center {
      #{$direction}: 10px;
      // 这边的 auto 做了什么？
      #{$reverse-direction}: auto;
    }

    &::before {
      @if $direction != center {
        #{$direction}: 9px;
      }
      @else {
        @extend %center-block;
      }
    }

    &::after {
      @if $direction != center {
        #{$direction}: 10px;
      }
      @else {
        @extend %center-block;
      }
    }
  }
}


// md-date-range-picker <-> mdrp
.mdrp-root {
  position: relative;
  display: inline-block;
}

$prefix-class: mdrp__panel;

.#{$prefix-class} {
    font-size: 15px;
    line-height: 1em;

    position: absolute;
    z-index: 3001;
    top: 100px;
    left: 20px;

    width: 278px;
    max-width: none;
    margin-top: 7px;
    padding: 0;

    color: inherit;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;

    &.show-calendar {
      display: block;
    }

    // TODO what these code do ?
    &::before,
    &::after {
      position: absolute;
      display: inline-block;
      border-bottom-color: red;
      content: "";
    }

    &::before {
      @include get-opens-arrow();
    }

    &::after {
      @include get-opens-arrow(6px, false);
    }

    & {
      @include control-opens-arrow-pos (left);
      @include control-opens-arrow-pos (right);
      @include control-opens-arrow-pos (center);
    }

    &.dropdown-menu {
      // TODO max-width: none; 什么作用？
      max-width: none;
      // TODO z-index best practice ？？
      z-index: 9;
    }

    .calendar-table {
      display: none;
      max-width: 270px;
    }

    &.show-calendar .calendar-table {
      display: block;
    }

}

/*  Larger Screen Styling */
@media (min-width: 564px) {
  // TODO width auto 的使用场景
  .#{$prefix-class} {
    width: auto;

    &.show-calendar {
      display: inline-flex;
    }

    .calendar-table {
      &.left {
        padding: 8px 0 8px 8px;
      }
      // 为何右边是 8px 8px 8px 0; 呢，也就是为何左边会有 8px ?
      &.right {
        padding: 8px;
      }
    }
  }
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
